import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Progress } from "@/components/ui/progress";
import { toast } from "sonner";
import { ArrowLeft, ArrowRight, ShieldCheck } from "lucide-react";
import BrandLogo from "@/components/BrandLogo";
import ConsultationFooter from "@/components/ConsultationFooter";

const GOAL_OPTIONS = ["Home", "Education", "Marriage", "Retirement", "Travel", "Wealth Growth", "Emergency Fund"];

export default function OnboardingForm() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({
    full_name: "",
    mobile: "",
    email: "",
    occupation: "",
    age: "",
    marital_status: "Single",
    current_investments_summary: "",
    financial_goals: [],
    financial_goals_note: "",
    monthly_income: "",
    monthly_expenditure: "",
    surplus_available_to_invest: "",
    current_insurance_health: { has_cover: false, sum_insured: "", insurer: "" },
    current_insurance_life: { has_cover: false, sum_insured: "", insurer: "" },
    expectations_from_us: "",
    marketing_opt_in: true,
  });

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const toggleGoal = (g) =>
    setForm((f) => ({
      ...f,
      financial_goals: f.financial_goals.includes(g)
        ? f.financial_goals.filter((x) => x !== g)
        : [...f.financial_goals, g],
    }));

  const submit = async () => {
    setBusy(true);
    try {
      const payload = {
        ...form,
        age: form.age ? Number(form.age) : null,
        monthly_income: form.monthly_income ? Number(form.monthly_income) : null,
        monthly_expenditure: form.monthly_expenditure ? Number(form.monthly_expenditure) : null,
        surplus_available_to_invest: form.surplus_available_to_invest ? Number(form.surplus_available_to_invest) : null,
        current_insurance_health: {
          has_cover: form.current_insurance_health.has_cover,
          sum_insured: form.current_insurance_health.sum_insured ? Number(form.current_insurance_health.sum_insured) : null,
          insurer: form.current_insurance_health.insurer || null,
        },
        current_insurance_life: {
          has_cover: form.current_insurance_life.has_cover,
          sum_insured: form.current_insurance_life.sum_insured ? Number(form.current_insurance_life.sum_insured) : null,
          insurer: form.current_insurance_life.insurer || null,
        },
        hp_website: form.hp_website || "",           // honeypot (must stay empty)
        captcha_token: form.captcha_token || null,   // reCAPTCHA v3 token if configured
      };
      const { data } = await api.post("/public/prospects", payload);
      toast.success("Thank you — let's book your Discovery Call");
      navigate(`/schedule/${data.token}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Submission failed");
    } finally {
      setBusy(false);
    }
  };

  const next = () => setStep((s) => Math.min(3, s + 1));
  const back = () => setStep((s) => Math.max(1, s - 1));
  const progress = (step / 3) * 100;

  return (
    <div className="marketing hero-bg min-h-screen">
      <header className="border-b border-black/5 bg-white/70 backdrop-blur-xl">
        <div className="mx-auto flex max-w-3xl items-center justify-between px-6 py-4">
          <a href="/" className="flex items-center gap-2" data-testid="brand-link">
            <BrandLogo size="sm" />
          </a>
          <span className="overline text-muted-foreground">Step {step} of 3</span>
        </div>
      </header>

      <div className="mx-auto max-w-3xl px-6 py-12">
        <div className="mb-6 flex items-center gap-3 text-sm text-primary">
          <ShieldCheck className="h-4 w-4" />
          A free, no-obligation Discovery Call. Nothing is sold on the call.
        </div>

        <h1 className="mb-3 text-3xl tracking-tight sm:text-4xl">Tell us a little about yourself.</h1>
        <p className="mb-8 text-muted-foreground">
          These basics help us have a useful first conversation. All fields are kept confidential.
        </p>

        <Progress value={progress} className="mb-8" data-testid="onboarding-progress" />

        <Card className="border-border bg-white p-8">
          {step === 1 && (
            <div className="space-y-5" data-testid="step-1">
              <h2 className="mb-2 font-dashboard text-xl font-bold">Basic details</h2>
              <div className="grid gap-4 md:grid-cols-2">
                <div>
                  <Label htmlFor="full_name">Full name *</Label>
                  <Input id="full_name" required value={form.full_name} onChange={(e) => set("full_name", e.target.value)} data-testid="input-full-name" />
                </div>
                <div>
                  <Label htmlFor="email">Email *</Label>
                  <Input id="email" type="email" required value={form.email} onChange={(e) => set("email", e.target.value)} data-testid="input-email" />
                </div>
                <div>
                  <Label htmlFor="mobile">Mobile *</Label>
                  <Input id="mobile" required value={form.mobile} onChange={(e) => set("mobile", e.target.value)} data-testid="input-mobile" />
                </div>
                <div>
                  <Label htmlFor="age">Age</Label>
                  <Input id="age" type="number" value={form.age} onChange={(e) => set("age", e.target.value)} data-testid="input-age" />
                </div>
                <div>
                  <Label htmlFor="occupation">Occupation</Label>
                  <Input id="occupation" value={form.occupation} onChange={(e) => set("occupation", e.target.value)} data-testid="input-occupation" />
                </div>
                <div>
                  <Label htmlFor="marital_status">Marital status</Label>
                  <select
                    id="marital_status"
                    className="mt-2 h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
                    value={form.marital_status}
                    onChange={(e) => set("marital_status", e.target.value)}
                    data-testid="input-marital-status"
                  >
                    <option>Single</option><option>Married</option><option>Divorced</option><option>Widowed</option>
                  </select>
                </div>
              </div>
              <div className="flex justify-end pt-4">
                <Button
                  onClick={next}
                  disabled={!form.full_name || !form.email || !form.mobile}
                  data-testid="step-1-next"
                >
                  Continue <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </div>
            </div>
          )}

          {step === 2 && (
            <div className="space-y-5" data-testid="step-2">
              <h2 className="mb-2 font-dashboard text-xl font-bold">Financial snapshot</h2>
              <div className="grid gap-4 md:grid-cols-3">
                <div>
                  <Label>Monthly income (₹)</Label>
                  <Input type="number" value={form.monthly_income} onChange={(e) => set("monthly_income", e.target.value)} data-testid="input-income" />
                </div>
                <div>
                  <Label>Monthly expenditure (₹)</Label>
                  <Input type="number" value={form.monthly_expenditure} onChange={(e) => set("monthly_expenditure", e.target.value)} data-testid="input-expenditure" />
                </div>
                <div>
                  <Label>Surplus to invest (₹)</Label>
                  <Input type="number" value={form.surplus_available_to_invest} onChange={(e) => set("surplus_available_to_invest", e.target.value)} data-testid="input-surplus" />
                </div>
              </div>
              <div>
                <Label>Current investments (brief summary)</Label>
                <Textarea value={form.current_investments_summary} onChange={(e) => set("current_investments_summary", e.target.value)} rows={3} data-testid="input-investments" />
              </div>
              <div className="grid gap-4 md:grid-cols-2">
                <div className="rounded-md border border-border p-4">
                  <div className="mb-2 flex items-center gap-2">
                    <Checkbox
                      id="hh"
                      checked={form.current_insurance_health.has_cover}
                      onCheckedChange={(v) => set("current_insurance_health", { ...form.current_insurance_health, has_cover: !!v })}
                      data-testid="input-has-health"
                    />
                    <Label htmlFor="hh" className="cursor-pointer">I have health cover</Label>
                  </div>
                  {form.current_insurance_health.has_cover && (
                    <div className="mt-3 space-y-3">
                      <Input placeholder="Sum insured (₹)" type="number" value={form.current_insurance_health.sum_insured} onChange={(e) => set("current_insurance_health", { ...form.current_insurance_health, sum_insured: e.target.value })} data-testid="input-health-sum" />
                      <Input placeholder="Insurer" value={form.current_insurance_health.insurer} onChange={(e) => set("current_insurance_health", { ...form.current_insurance_health, insurer: e.target.value })} data-testid="input-health-insurer" />
                    </div>
                  )}
                </div>
                <div className="rounded-md border border-border p-4">
                  <div className="mb-2 flex items-center gap-2">
                    <Checkbox
                      id="hl"
                      checked={form.current_insurance_life.has_cover}
                      onCheckedChange={(v) => set("current_insurance_life", { ...form.current_insurance_life, has_cover: !!v })}
                      data-testid="input-has-life"
                    />
                    <Label htmlFor="hl" className="cursor-pointer">I have life cover</Label>
                  </div>
                  {form.current_insurance_life.has_cover && (
                    <div className="mt-3 space-y-3">
                      <Input placeholder="Sum insured (₹)" type="number" value={form.current_insurance_life.sum_insured} onChange={(e) => set("current_insurance_life", { ...form.current_insurance_life, sum_insured: e.target.value })} data-testid="input-life-sum" />
                      <Input placeholder="Insurer" value={form.current_insurance_life.insurer} onChange={(e) => set("current_insurance_life", { ...form.current_insurance_life, insurer: e.target.value })} data-testid="input-life-insurer" />
                    </div>
                  )}
                </div>
              </div>
              <div className="flex justify-between pt-4">
                <Button variant="outline" onClick={back} data-testid="step-2-back"><ArrowLeft className="mr-2 h-4 w-4" /> Back</Button>
                <Button onClick={next} data-testid="step-2-next">Continue <ArrowRight className="ml-2 h-4 w-4" /></Button>
              </div>
            </div>
          )}

          {step === 3 && (
            <div className="space-y-5" data-testid="step-3">
              <h2 className="mb-2 font-dashboard text-xl font-bold">Goals &amp; expectations</h2>
              <div>
                <Label>Which of these matter to you?</Label>
                <div className="mt-3 flex flex-wrap gap-2">
                  {GOAL_OPTIONS.map((g) => (
                    <button
                      key={g}
                      type="button"
                      onClick={() => toggleGoal(g)}
                      data-testid={`goal-${g.toLowerCase().replace(/\s+/g, "-")}`}
                      className={
                        "rounded-full border px-4 py-1.5 text-sm transition-colors duration-200 " +
                        (form.financial_goals.includes(g)
                          ? "border-primary bg-primary text-primary-foreground"
                          : "border-border bg-white text-foreground hover:bg-secondary")
                      }
                    >
                      {g}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <Label>Anything else about your goals?</Label>
                <Textarea value={form.financial_goals_note} onChange={(e) => set("financial_goals_note", e.target.value)} rows={3} data-testid="input-goals-note" />
              </div>
              <div>
                <Label>What do you expect from us?</Label>
                <Textarea value={form.expectations_from_us} onChange={(e) => set("expectations_from_us", e.target.value)} rows={3} data-testid="input-expectations" />
              </div>
              <div className="flex items-center gap-2">
                <Checkbox id="opt" checked={form.marketing_opt_in} onCheckedChange={(v) => set("marketing_opt_in", !!v)} data-testid="input-opt-in" />
                <Label htmlFor="opt" className="cursor-pointer">I&apos;d like to receive occasional educational updates</Label>
              </div>
              {/* Honeypot — must remain hidden and empty */}
              <input
                type="text"
                tabIndex={-1}
                autoComplete="off"
                value={form.hp_website || ""}
                onChange={(e) => set("hp_website", e.target.value)}
                aria-hidden="true"
                style={{ position: "absolute", left: "-9999px", width: "1px", height: "1px", opacity: 0 }}
                name="hp_website"
              />
              <div className="flex justify-between pt-4">
                <Button variant="outline" onClick={back} data-testid="step-3-back"><ArrowLeft className="mr-2 h-4 w-4" /> Back</Button>
                <Button onClick={submit} disabled={busy} data-testid="submit-onboarding">
                  {busy ? "Submitting…" : "Submit & book Discovery Call"} <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </div>
              <div className="pt-6 text-center text-sm">
                <a
                  href="https://reliservewealth.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primary underline underline-offset-4 hover:text-primary/80"
                  data-testid="back-to-main-site"
                >
                  ← Back to reliservewealth.com
                </a>
              </div>
            </div>
          )}
        </Card>
      </div>

      <ConsultationFooter />
    </div>
  );
}
